// Cube geometry, rebuilt from the block model: only faces exposed to air are
// emitted. Which atlas tile a face gets follows the cell's *onion depth* and
// the face's orientation within its shell (`render/onion.ts`) — grass on the
// crust, magma underneath — exactly as the original's per-zone block meshes
// bake it. Infection is a tint on top of that tile, not a tile of its own.
//
// Two meshes come out of here: the opaque cube, and the translucent ghost
// shells destroyed blocks leave behind. Both go through `emitFace`, so both sit
// on the same rounded box — a ghost built from raw axis-aligned quads pokes out
// past the cube's silhouette at every rounded edge, which is exactly what it
// looked like before this was shared.

import type { MeshData } from "./webgl";
import type { BlocksModel } from "./game/blocks";
import { faceRole, tileFor, zoneDepth } from "./render/onion";

export const GRID_SIZE = 5;

interface Face {
  readonly n: [number, number, number];
  readonly corners: [number, number, number][];
}

// Per-direction face template: outward normal + the 4 cell-corner offsets
// (counter-clockwise seen from outside), matching UV order (0,0)(1,0)(1,1)(0,1).
const FACES: Face[] = [
  { n: [0, 0, 1], corners: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]] },
  { n: [0, 0, -1], corners: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]] },
  { n: [1, 0, 0], corners: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]] },
  { n: [-1, 0, 0], corners: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]] },
  { n: [0, 1, 0], corners: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]] },
  { n: [0, -1, 0], corners: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]] },
];

// Faces are subdivided so vertices near the cube's outer edges can bend
// around the rounding; SUBDIV sub-quads per edge keeps the curve smooth.
const SUBDIV = 4;
// Rounding radius of the outer edges, as a fraction of one cell.
// Measured off a production screenshot: corner arc ~56px at ~112px per block.
const EDGE_RADIUS_CELLS = 0.5;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Accumulating vertex/index arrays, filled by `emitFace`. */
interface Builder {
  positions: number[];
  normals: number[];
  uvs: number[];
  materials: number[];
  tints: number[];
  indices: number[];
}

function builder(): Builder {
  return { positions: [], normals: [], uvs: [], materials: [], tints: [], indices: [] };
}

function finish(b: Builder): MeshData {
  return {
    positions: new Float32Array(b.positions),
    normals: new Float32Array(b.normals),
    uvs: new Float32Array(b.uvs),
    materials: new Float32Array(b.materials),
    tints: new Float32Array(b.tints),
    indices: new Uint16Array(b.indices),
  };
}

/** Geometry constants shared by the two meshes, derived from the cube's size. */
interface Frame {
  readonly cell: number;
  readonly origin: number;
  readonly radius: number;
  /** Half-size of the box the rounding is swept around. */
  readonly inner: number;
}

function frameFor(size: number, n: number): Frame {
  const cell = size / n;
  const radius = cell * EDGE_RADIUS_CELLS;
  return { cell, origin: -size / 2, radius, inner: size / 2 - radius };
}

/**
 * Emit one subdivided, rounded face of the cell at `(x, y, z)`.
 *
 * Vertices are clamped onto a rounded box: anything further than `radius` from
 * the inner box is pulled back onto its surface. `onHull` says whether the face
 * lies on the cube's outer skin — only those get the radial normal that shades
 * the rounding smoothly; interior walls that merely graze an edge keep their
 * flat normal.
 */
function emitFace(
  b: Builder,
  f: Frame,
  face: Face,
  x: number,
  y: number,
  z: number,
  onHull: boolean,
  material: number,
  tint: number,
): void {
  const [nx, ny, nz] = face.n;
  const c = face.corners.map(([cx, cy, cz]): [number, number, number] => [
    f.origin + (x + cx) * f.cell,
    f.origin + (y + cy) * f.cell,
    f.origin + (z + cz) * f.cell,
  ]);

  const base = b.positions.length / 3;
  for (let j = 0; j <= SUBDIV; j++) {
    const v = j / SUBDIV;
    for (let i = 0; i <= SUBDIV; i++) {
      const u = i / SUBDIV;
      const w0 = (1 - u) * (1 - v);
      const w1 = u * (1 - v);
      const w2 = u * v;
      const w3 = (1 - u) * v;
      let px = w0 * c[0][0] + w1 * c[1][0] + w2 * c[2][0] + w3 * c[3][0];
      let py = w0 * c[0][1] + w1 * c[1][1] + w2 * c[2][1] + w3 * c[3][1];
      let pz = w0 * c[0][2] + w1 * c[1][2] + w2 * c[2][2] + w3 * c[3][2];

      const dx = px - clamp(px, -f.inner, f.inner);
      const dy = py - clamp(py, -f.inner, f.inner);
      const dz = pz - clamp(pz, -f.inner, f.inner);
      const len = Math.hypot(dx, dy, dz);
      let vnx = nx;
      let vny = ny;
      let vnz = nz;
      if (len > f.radius) {
        const s = f.radius / len;
        px += dx * (s - 1);
        py += dy * (s - 1);
        pz += dz * (s - 1);
        if (onHull) {
          vnx = dx / len;
          vny = dy / len;
          vnz = dz / len;
        }
      }

      b.positions.push(px, py, pz);
      b.normals.push(vnx, vny, vnz);
      b.uvs.push(u, v);
      b.materials.push(material);
      b.tints.push(tint);
    }
  }
  for (let j = 0; j < SUBDIV; j++) {
    for (let i = 0; i < SUBDIV; i++) {
      const a = base + j * (SUBDIV + 1) + i;
      const bb = a + 1;
      const d = a + SUBDIV + 2;
      const e = a + SUBDIV + 1;
      b.indices.push(a, bb, d, a, d, e);
    }
  }
}

/** Whether a neighbour cell lies outside the grid — i.e. the face is on the skin. */
function outOfBounds(ax: number, ay: number, az: number, n: number): boolean {
  return ax < 0 || ax >= n || ay < 0 || ay >= n || az < 0 || az >= n;
}

/**
 * Shell geometry for every `ghost` cell — the translucent husk a destroyed
 * block leaves behind (`0_subblock_ghost_1.prefab`). Drawn in a separate
 * blended pass, so it is a separate mesh; its tile and tint are unused, because
 * the original's ghost material has no texture at all (`unity-overview.md` §6).
 *
 * A face buried against a *solid* neighbour is dropped, but a face against
 * another ghost is kept: the original instantiates one whole prefab per ghost,
 * so a run of them still reads as separate cells rather than one slab.
 */
export function buildGhostGeometry(grid: BlocksModel, size = 1): MeshData {
  const n = grid.size;
  const f = frameFor(size, n);
  const b = builder();

  for (let z = 0; z < n; z++) {
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (grid.state(x, y, z) !== "ghost") continue;
        for (const face of FACES) {
          const [nx, ny, nz] = face.n;
          const ax = x + nx;
          const ay = y + ny;
          const az = z + nz;
          if (grid.isSolid(ax, ay, az)) continue;
          emitFace(b, f, face, x, y, z, outOfBounds(ax, ay, az, n), 0, 0);
        }
      }
    }
  }
  return finish(b);
}

export function buildGridGeometry(grid: BlocksModel, size = 1): MeshData {
  const n = grid.size;
  const f = frameFor(size, n);
  const b = builder();

  for (let z = 0; z < n; z++) {
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (!grid.isSolid(x, y, z)) continue;
        for (const face of FACES) {
          const [nx, ny, nz] = face.n;
          const ax = x + nx;
          const ay = y + ny;
          const az = z + nz;
          if (grid.isSolid(ax, ay, az)) continue; // hidden interior face
          // Tile follows depth + orientation, so a face uncovered by eating
          // shows the layer beneath rather than generic stone.
          const material = tileFor(zoneDepth(x, y, z, n), faceRole(x, y, z, n, face.n));
          const tint = grid.state(x, y, z) === "infected" ? 1 : 0;
          emitFace(b, f, face, x, y, z, outOfBounds(ax, ay, az, n), material, tint);
        }
      }
    }
  }
  return finish(b);
}
